import { isDefined } from "@clipboard-health/util-ts";
import { post } from "@src/appV2/api";
import { environmentConfig } from "@src/appV2/environment";
import { isCbhApiAxiosError } from "@src/appV2/Errors/errorUtils";
import { APP_V2_APP_EVENTS } from "@src/appV2/lib";
import { useMutation, type UseMutationOptions, useQueryClient } from "@tanstack/react-query";
import { type AxiosError } from "axios";
import { z } from "zod";

import { type UpdateFavoriteWorkplacesAnalytics } from "../../redesign/ShiftDiscovery/FavoriteWorkplaces/types";
import { getFavoriteWorkplacesBaseQueryKey } from "./useGetFavoriteWorkplaces";

const addFavoriteWorkplaceRequestSchema = z.object({
  data: z.object({
    type: z.literal("favorite-workplace"),
    attributes: z.object({
      workerId: z.string(),
      workplaceId: z.string(),
    }),
  }),
});

const addFavoriteWorkplaceResponseSchema = z.object({
  data: z.object({
    type: z.literal("favorite-workplace"),
    id: z.string(),
    attributes: z.object({
      workerId: z.string(),
      workplaceId: z.string(),
      createdAt: z.string(),
      updatedAt: z.string(),
    }),
    relationships: z.object({
      worker: z.object({
        data: z.object({
          type: z.string(),
          id: z.string(),
        }),
      }),
      workplace: z.object({
        data: z.object({
          type: z.string(),
          id: z.string(),
        }),
      }),
    }),
  }),
});

export type AddFavoriteWorkplaceRequest = z.infer<typeof addFavoriteWorkplaceRequestSchema>;
export type AddFavoriteWorkplaceResponse = z.infer<typeof addFavoriteWorkplaceResponseSchema>;

export interface AddFavoriteWorkplaceParams {
  workerId: string;
  workplaceId: string;
  analytics: UpdateFavoriteWorkplacesAnalytics;
}

// Consider adding max limit validation to client-side to avoid server error dependency
export function getAddFavoriteWorkplaceErrorMessage(error: unknown): string {
  if (isCbhApiAxiosError(error)) {
    const message = error.response.data.errors.find((error) => error.detail)?.detail;
    if (isDefined(message)) {
      return message;
    }
  }

  return "Failed to add workplace to favorites";
}

export function useAddFavoriteWorkplace(
  options: UseMutationOptions<
    AddFavoriteWorkplaceResponse,
    AxiosError,
    AddFavoriteWorkplaceParams
  > = {}
) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (params: AddFavoriteWorkplaceParams) => {
      const requestData: AddFavoriteWorkplaceRequest = {
        data: {
          type: "favorite-workplace",
          attributes: {
            workerId: params.workerId,
            workplaceId: params.workplaceId,
          },
        },
      };

      const response = await post({
        url: `${environmentConfig.REACT_APP_BASE_API_URL}/favorite-workplaces`,
        data: requestData,
        requestSchema: addFavoriteWorkplaceRequestSchema,
        responseSchema: addFavoriteWorkplaceResponseSchema,
      });

      return response.data;
    },
    meta: {
      logErrorMessage: APP_V2_APP_EVENTS.ADD_FAVORITE_WORKPLACE_FAILURE,
    },
    ...options,
    onSettled: (...params) => {
      // We don't want anyone to override this, so we call it before the custom onSettled
      void queryClient.invalidateQueries({
        queryKey: getFavoriteWorkplacesBaseQueryKey(),
      });
      return options.onSettled?.(...params);
    },
  });
}
